import { Link, useLocation } from 'react-router-dom';
import styles from './Breadcrumbs.module.css';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  showHome?: boolean;
}

// Route label mappings
const routeLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  records: 'Staff Records',
  tasks: 'My Tasks',
  search: 'Search',
  reports: 'Reports',
  'recycle-bin': 'Recycle Bin',
  admin: 'Administration',
  users: 'Users',
  departments: 'Departments',
  roles: 'Roles',
  workflows: 'Workflows',
  settings: 'Settings',
  profile: 'Profile',
};

export function Breadcrumbs({ items, showHome = true }: BreadcrumbsProps) {
  const location = useLocation();

  // If items are provided, use them directly
  if (items) {
    return (
      <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
        <ol className={styles.list}>
          {showHome && (
            <li className={styles.item}>
              <Link to="/dashboard" className={styles.link}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M3 10L10 3L17 10M5 8.5V16.5C5 16.7761 5.22386 17 5.5 17H8.5V12.5C8.5 12.2239 8.72386 12 9 12H11C11.2761 12 11.5 12.2239 11.5 12.5V17H14.5C14.7761 17 15 16.7761 15 16.5V8.5" />
                </svg>
                <span>Home</span>
              </Link>
              <ChevronIcon />
            </li>
          )}
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            return (
              <li key={index} className={styles.item}>
                {isLast || !item.path ? (
                  <span className={styles.current} aria-current="page">
                    {item.label}
                  </span>
                ) : (
                  <>
                    <Link to={item.path} className={styles.link}>
                      {item.label}
                    </Link>
                    <ChevronIcon />
                  </>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }

  // Auto-generate breadcrumbs from current path
  const pathSegments = location.pathname.split('/').filter(Boolean);

  if (pathSegments.length === 0) {
    return null;
  }

  // Skip if we're on dashboard (home)
  if (pathSegments.length === 1 && pathSegments[0] === 'dashboard') {
    return null;
  }

  return (
    <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
      <ol className={styles.list}>
        {showHome && (
          <li className={styles.item}>
            <Link to="/dashboard" className={styles.link}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M3 10L10 3L17 10M5 8.5V16.5C5 16.7761 5.22386 17 5.5 17H8.5V12.5C8.5 12.2239 8.72386 12 9 12H11C11.2761 12 11.5 12.2239 11.5 12.5V17H14.5C14.7761 17 15 16.7761 15 16.5V8.5" />
              </svg>
              <span>Home</span>
            </Link>
            <ChevronIcon />
          </li>
        )}
        {pathSegments.map((segment, index) => {
          const path = '/' + pathSegments.slice(0, index + 1).join('/');
          const label = routeLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
          const isLast = index === pathSegments.length - 1;

          return (
            <li key={path} className={styles.item}>
              {isLast ? (
                <span className={styles.current} aria-current="page">
                  {label}
                </span>
              ) : (
                <>
                  <Link to={path} className={styles.link}>
                    {label}
                  </Link>
                  <ChevronIcon />
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ChevronIcon() {
  return (
    <svg
      className={styles.chevron}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M4.5 2.5L8 6L4.5 9.5" />
    </svg>
  );
}
